import { z } from 'zod';
import { subscriptionStatusSchema } from './platform';

/** Data lifecycle: tenant export, cancellation/offboarding and retention (ADR-0034). */

export const tenantExportStatusSchema = z.enum([
  'pending',
  'running',
  'ready',
  'failed',
  'expired',
]);
export type TenantExportStatus = z.infer<typeof tenantExportStatusSchema>;

export const tenantExportSchema = z.object({
  id: z.string(),
  status: tenantExportStatusSchema,
  requested_by: z.string(),
  requested_by_name: z.string().nullable(),
  size_bytes: z.number().int().nullable(),
  error: z.string().nullable(),
  created_at: z.string(),
  completed_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  /** Ready and not yet expired: a download link can be requested. */
  downloadable: z.boolean(),
});
export type TenantExport = z.infer<typeof tenantExportSchema>;

export const tenantExportListSchema = z.object({
  items: z.array(tenantExportSchema),
  /** Exports allowed per rolling 24 hours, and how many were requested in that window. */
  limit_per_day: z.number().int(),
  requested_last_24h: z.number().int(),
});
export type TenantExportList = z.infer<typeof tenantExportListSchema>;

export const exportDownloadLinkSchema = z.object({
  /** Same-origin, HMAC-signed, short-lived (≤ 15 minutes) download URL. */
  url: z.string(),
  expires_at: z.string(),
});
export type ExportDownloadLink = z.infer<typeof exportDownloadLinkSchema>;

/** Retention by data class, platform setting `retention` (legal review required). */
export const retentionPolicySchema = z.object({
  cancellation_grace_days: z.number().int().min(0).max(365),
  export_ttl_days: z.number().int().min(1).max(30),
  sessions_days: z.number().int().min(1).max(365),
  auth_tokens_days: z.number().int().min(1).max(365),
  email_outbox_days: z.number().int().min(7).max(730),
  guest_personal_data_days: z.number().int().min(30).max(3650),
  financial_records_years: z.number().int().min(10).max(30),
  audit_logs_years: z.number().int().min(1).max(30),
  /** Nightly backup dumps are kept this long (R2 lifecycle rule); informational. */
  backup_retention_days: z.number().int().min(30).max(365),
});
export type RetentionPolicyDto = z.infer<typeof retentionPolicySchema>;

/** Partial update: built from the field shapes, so omitted fields are never reset. */
export const updateRetentionPolicyRequestSchema = retentionPolicySchema.partial();

export const dataOverviewSchema = z.object({
  tenant: z.object({ id: z.string(), slug: z.string(), name: z.string() }),
  subscription_status: subscriptionStatusSchema,
  cancelled_at: z.string().nullable(),
  deletion_scheduled_at: z.string().nullable(),
  retention: retentionPolicySchema,
});
export type DataOverview = z.infer<typeof dataOverviewSchema>;

export const cancelTenantRequestSchema = z.object({
  /** The tenant's slug, typed by the owner to confirm. */
  confirm_slug: z.string().trim().min(1).max(64),
  reason: z.string().trim().min(3).max(500),
});
export type CancelTenantRequest = z.input<typeof cancelTenantRequestSchema>;

// ---- Platform (operators)

export const deletionCertificateSchema = z.object({
  certificate_id: z.string(),
  tenant_id: z.string(),
  cancelled_at: z.string().nullable(),
  deletion_scheduled_at: z.string().nullable(),
  started_at: z.string(),
  completed_at: z.string(),
  tables: z.record(z.string(), z.number().int()),
  total_rows: z.number().int(),
  users_anonymised: z.number().int(),
  sessions_deleted: z.number().int(),
  r2_objects_deleted: z.number().int(),
  retained: z.record(z.string(), z.number().int()),
  backups: z.object({
    d1_time_travel_days: z.number().int(),
    nightly_backup_days: z.number().int(),
    data_may_remain_in_backups_until: z.string(),
  }),
});
export type DeletionCertificate = z.infer<typeof deletionCertificateSchema>;

export const platformLifecycleTenantSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  subscription_status: subscriptionStatusSchema,
  cancelled_at: z.string().nullable(),
  deletion_scheduled_at: z.string().nullable(),
  /** Set once the deletion certificate exists. */
  purged_at: z.string().nullable(),
  certificate: deletionCertificateSchema.nullable(),
});
export type PlatformLifecycleTenant = z.infer<typeof platformLifecycleTenantSchema>;

export const platformLifecycleTenantListSchema = z.object({
  items: z.array(platformLifecycleTenantSchema),
});

export const restoreTenantRequestSchema = z.object({
  /** Lifecycle status after the restore. */
  status: z.enum(['active', 'trialing', 'past_due', 'read_only']),
  reason: z.string().trim().min(3).max(500),
});

export const purgeTenantRequestSchema = z.object({
  /** The tenant's slug, typed by the operator to confirm. */
  confirm_slug: z.string().trim().min(1).max(64),
  reason: z.string().trim().min(3).max(500),
});

export const purgeTenantResponseSchema = z.object({
  status: z.enum(['purged', 'incomplete']),
  certificate: deletionCertificateSchema.nullable(),
});
export type PurgeTenantResponse = z.infer<typeof purgeTenantResponseSchema>;
